import { Popover } from 'antd';
import Edit from 'assets/Icons/edit';
import Text from 'components/Text';
import PinCodeModal, {
  PinCodeModalRef,
} from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/PinCodeModal';
import useControlCustomerInfoPopover from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/hooks/useControlCustomerInfoPopover';
import { CustomerInfoPopoverUI } from 'features/user/widgets/WaitingList/WalkIn/components/CustomerName';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { ICustomerItemData } from 'services/shop/types/customer';
import styled from 'styled-components';
import { maskPhone } from 'utils/formatPhone';
import { unknownCustomerId } from './CustomerSearchInput';
import colorTheme from 'constants/color';
import Empty from 'components/Empty';
import { ic_favorite, ic_health, ic_note, ic_preferances } from '../icons';

type Props = {
  value?: ICustomerItemData;
};

const overlayInnerStyle = {
  padding: 16,
  borderRadius: 8,
  background: '#E4F4F6',
};

const CustomerBox = ({ value }: Props) => {
  const pinCodeModalRef = useRef<PinCodeModalRef>(null);
  const customerId =
    !value?.id || value?.id === unknownCustomerId ? '' : value.id;
  const {
    customerInfo,
    customerDetailLoading,
    open,
    pinData,
    onEdit,
    handleClose,
    fetchCustomerDetail,
    handleEditAfterCheckPinCode,
  } = useControlCustomerInfoPopover(customerId);

  useEffect(() => {
    if(!customerId) return;
    fetchCustomerDetail();
  }, [customerId]);

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    pinCodeModalRef.current?.open((data) => {
      handleEditAfterCheckPinCode(data);
    });
  };

  return (
    <>
      {customerId ? (
        <Container>
          <Card>
            <HeaderRow>
              <Text variant='H5'>{value?.name || '--'}</Text>
              {!!customerId && (
                <InfoButton type='button' onClick={handleEdit}>
                  <Edit fill={colorTheme.v2_secondary_3} />
                </InfoButton>
              )}
            </HeaderRow>
            <Row>
              <Label>Phone:</Label>
              <Value>
                {value?.phone
                  ? maskPhone(value?.phone)
                  : '--'}
              </Value>
            </Row>
            <Row>
              <Label>Email:</Label>
              <Value>{value?.email || '--'}</Value>
            </Row>
            <Row>
              <Label>DOB:</Label>
              <Value>
                {value?.birthday
                  ? moment(value?.birthday).format('MM-DD')
                  : '--'}
              </Value>
            </Row>
            <Row>
              <Label>Created Date:</Label>
              <Value>
                {value?.createdDate
                  ? moment(value?.createdDate).format('MM-DD-YYYY')
                  : '--'}
              </Value>
            </Row>
            <Row>
              <Label>Last Visit Date:</Label>
              <Value>
                {value?.lastVisitedDate
                  ? moment(value?.lastVisitedDate).format('MM-DD-YYYY')
                  : '--'}
              </Value>
            </Row>
            <Row>
              <Label style={{ color: colorTheme.v2_primary_bg_6, fontWeight: 'bold' }}>Visited:</Label>
              <Value style={{ color: colorTheme.v2_primary_bg_6, fontWeight: 'bold' }}>{value?.numberVisit || '--'}</Value>
            </Row>
          </Card>
          {!!customerInfo?.customerNote && <SectionCard>
            <SectionTitle>Note</SectionTitle>
            <SectionValue>{customerInfo?.customerNote || '--'}</SectionValue>
            <SectionIcon>
              <IconImg src={ic_note} alt='note' />
            </SectionIcon>
          </SectionCard>}
          {!!customerInfo?.customerHobbies && <SectionCard>
            <SectionTitle>Preferences</SectionTitle>
            <SectionValue>{customerInfo?.customerHobbies || '--'}</SectionValue>
            <SectionIcon>
              <IconImg src={ic_preferances} alt='preferances' />
            </SectionIcon>
          </SectionCard>}
          {!!customerInfo?.customerHealth && <SectionCard>
            <SectionTitle>Health Conditions</SectionTitle>
            <SectionValue>{customerInfo?.customerHealth || '--'}</SectionValue>
            <SectionIcon>
              <IconImg src={ic_health} alt='health' />
            </SectionIcon>
          </SectionCard>}
          {customerInfo?.customerColor && <SectionCard>
            <SectionTitle>Favorite Polish Colors</SectionTitle>
            <SectionValue>{customerInfo?.customerColor || '--'}</SectionValue>
            <SectionIcon>
              <IconImg src={ic_favorite} alt='favorite' />
            </SectionIcon>
          </SectionCard>}
        </Container>
      ) : <WrapperEmpty><Empty description='Please select customer' /></WrapperEmpty>}

      <Popover
        placement='rightTop'
        title={null}
        content={
          <div onClick={(e) => e.stopPropagation()}>
            <CustomerInfoPopoverUI
              loading={customerDetailLoading}
              customerInfo={customerInfo}
              onOpenChange={handleClose}
              onEdit={onEdit}
              dataPin={pinData}
            />
          </div>
        }
        open={open}
        trigger={'click'}
        overlayInnerStyle={overlayInnerStyle}
      >
        <span />
      </Popover>

      <PinCodeModal ref={pinCodeModalRef} title='ADMIN PASSWORD' />
    </>
  );
};

export default CustomerBox;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WrapperEmpty = styled.div`
flex: 1;
display: flex;
align-items: center;
justify-content: center;
`;


const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Card = styled.div`
  margin: 0 8px;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
`;

const Label = styled.div`
  color: #1d2129;
  font-weight: 600;
`;

const Value = styled.div`
  color: #1d2129;
`;

const InfoButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
`;

const SectionCard = styled.div`
  margin: 0 8px;
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const SectionTitle = styled.div`
  color: #1d2129;
  font-weight: 600;
  min-width: 160px;
`;

const SectionValue = styled.div`
  color: #1d2129;
  flex: 1;
`;

const SectionIcon = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const IconImg = styled.img`
  width: 32px;
  height: 32px;
`;